/**
 * Custom React Hook: usePianoRollEditor
 * Handles selection, dragging, resizing, note auditioning, deletion, adding, and velocity editing
 */

import { useCallback, useState } from 'react';
import { AudioEngine } from '../core/audioEngine';
import { getGridTicks, snapTickToGrid } from '../core/quantize';
import { MidiNote, QuantizeGrid } from '../types/midi';

export interface UsePianoRollEditorOptions {
  notes: MidiNote[];
  onChangeNotes: (notes: MidiNote[]) => void;
  audioEngine: AudioEngine;
  grid: QuantizeGrid;
  ppq: number;
}

export function usePianoRollEditor({
  notes,
  onChangeNotes,
  audioEngine,
  grid,
  ppq,
}: UsePianoRollEditorOptions) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Note selection
  const selectNote = useCallback((id: string, multi = false) => {
    setSelectedIds((prev) => {
      const next = new Set(multi ? prev : []);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(notes.map((n) => n.id)));
  }, [notes]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const isSelected = useCallback(
    (id: string) => selectedIds.has(id),
    [selectedIds]
  );

  // Audition note helper
  const auditionPitch = useCallback(
    (pitch: number, velocity = 90) => {
      audioEngine.playNote(pitch, velocity, 0.25);
    },
    [audioEngine]
  );

  // Delete selected notes
  const deleteSelected = useCallback(() => {
    if (selectedIds.size === 0) return;
    const remaining = notes.filter((n) => !selectedIds.has(n.id));
    onChangeNotes(remaining);
    setSelectedIds(new Set());
  }, [notes, selectedIds, onChangeNotes]);

  // Delete single note
  const deleteNote = useCallback(
    (id: string) => {
      const remaining = notes.filter((n) => n.id !== id);
      onChangeNotes(remaining);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    },
    [notes, onChangeNotes]
  );

  // Add a new note at position
  const addNote = useCallback(
    (pitch: number, rawTick: number, duration?: number, velocity = 96) => {
      const snappedStart = snapTickToGrid(rawTick, grid, ppq);
      const defaultDuration = duration || getGridTicks(grid, ppq) || 240;
      const newNote: MidiNote = {
        id: `note-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
        pitch: Math.max(0, Math.min(127, pitch)),
        velocity: Math.max(1, Math.min(127, velocity)),
        startTick: snappedStart,
        durationTicks: Math.max(20, defaultDuration),
        channel: 1,
        rawStartTick: snappedStart,
        rawDurationTicks: Math.max(20, defaultDuration),
      };

      auditionPitch(newNote.pitch, newNote.velocity);
      onChangeNotes([...notes, newNote]);
      setSelectedIds(new Set([newNote.id]));
      return newNote;
    },
    [grid, ppq, notes, onChangeNotes, auditionPitch]
  );

  // Move notes (time & pitch)
  const moveNotes = useCallback(
    (targetIds: string[], deltaTicks: number, deltaPitch: number) => {
      if (targetIds.length === 0) return;
      const idSet = new Set(targetIds);

      let audited = false;

      const updated = notes.map((note) => {
        if (!idSet.has(note.id)) return note;

        const newPitch = Math.max(0, Math.min(127, note.pitch + deltaPitch));
        if (!audited && deltaPitch !== 0) {
          auditionPitch(newPitch, note.velocity);
          audited = true;
        }

        const rawStart = (note.rawStartTick ?? note.startTick) + deltaTicks;
        const newStart = Math.max(0, grid !== 'Off' ? snapTickToGrid(rawStart, grid, ppq) : Math.round(rawStart));

        return {
          ...note,
          pitch: newPitch,
          startTick: newStart,
          rawStartTick: newStart,
          manuallyEdited: true,
        };
      });

      onChangeNotes(updated);
    },
    [notes, grid, ppq, onChangeNotes, auditionPitch]
  );

  // Resize notes duration (supports left and right edge resizing)
  const resizeNotes = useCallback(
    (targetIds: string[], deltaTicks: number, direction: 'left' | 'right' = 'right') => {
      if (targetIds.length === 0) return;
      const idSet = new Set(targetIds);

      const updated = notes.map((note) => {
        if (!idSet.has(note.id)) return note;

        const baseStart = note.startTick;
        const baseDur = note.durationTicks;
        const endTick = baseStart + baseDur;

        if (direction === 'left') {
          const targetStart = baseStart + deltaTicks;
          const snappedStart = grid !== 'Off' ? snapTickToGrid(targetStart, grid, ppq) : Math.round(targetStart);
          const clampedStart = Math.max(0, Math.min(endTick - 20, snappedStart));
          const newDur = endTick - clampedStart;
          return {
            ...note,
            startTick: clampedStart,
            rawStartTick: clampedStart,
            durationTicks: newDur,
            rawDurationTicks: newDur,
            manuallyEdited: true,
          };
        } else {
          const targetEnd = endTick + deltaTicks;
          const snappedEnd = grid !== 'Off' ? snapTickToGrid(targetEnd, grid, ppq) : Math.round(targetEnd);
          const newDur = Math.max(20, snappedEnd - baseStart);
          return {
            ...note,
            durationTicks: newDur,
            rawDurationTicks: newDur,
            manuallyEdited: true,
          };
        }
      });

      onChangeNotes(updated);
    },
    [notes, grid, ppq, onChangeNotes]
  );

  // Update velocity for single or multiple notes
  const setNoteVelocity = useCallback(
    (noteId: string, newVelocity: number) => {
      const clampedVel = Math.max(1, Math.min(127, Math.round(newVelocity)));
      const isTargetSelected = selectedIds.has(noteId);

      const updated = notes.map((note) => {
        if (note.id === noteId || (isTargetSelected && selectedIds.has(note.id))) {
          return {
            ...note,
            velocity: clampedVel,
          };
        }
        return note;
      });

      onChangeNotes(updated);
    },
    [notes, selectedIds, onChangeNotes]
  );

  // Split a note in two at a given tick (razor tool) — keeps both halves selected
  const splitNoteAt = useCallback(
    (id: string, atTick: number) => {
      const target = notes.find((n) => n.id === id);
      if (!target) return;

      const minPieceTicks = 20;
      const snappedAt = grid !== 'Off' ? snapTickToGrid(atTick, grid, ppq) : Math.round(atTick);
      const splitTick = Math.max(
        target.startTick + minPieceTicks,
        Math.min(target.startTick + target.durationTicks - minPieceTicks, snappedAt)
      );
      // Note too short to produce two valid pieces — nothing to split.
      if (splitTick <= target.startTick || splitTick >= target.startTick + target.durationTicks) return;

      const firstDuration = splitTick - target.startTick;
      const secondDuration = target.startTick + target.durationTicks - splitTick;

      const firstHalf: MidiNote = {
        ...target,
        durationTicks: firstDuration,
        rawDurationTicks: firstDuration,
        manuallyEdited: true,
      };
      const secondHalf: MidiNote = {
        ...target,
        id: `note-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
        startTick: splitTick,
        rawStartTick: splitTick,
        durationTicks: secondDuration,
        rawDurationTicks: secondDuration,
        manuallyEdited: true,
      };

      onChangeNotes([...notes.filter((n) => n.id !== id), firstHalf, secondHalf]);
      setSelectedIds(new Set([firstHalf.id, secondHalf.id]));
    },
    [notes, grid, ppq, onChangeNotes]
  );

  return {
    selectedIds,
    selectNote,
    selectAll,
    clearSelection,
    isSelected,
    deleteSelected,
    deleteNote,
    addNote,
    moveNotes,
    resizeNotes,
    splitNoteAt,
    setNoteVelocity,
    auditionPitch,
  };
}
